/* ============================================================================
   Stickr Lab — Sticker Sheet Layout & Yield Engine v1  (assets/sl-sheets.js)

   Deterministic layout/yield calculation for kiss-cut sticker sheets. Implements
   "Sticker Sheets — Layout & Yield Specification v1" (approved) and decisions
   D-024 / D-030 / D-031 / D-032.

   Framework-independent: no React, no Shopify, no DOM. Safe to consume from the
   Builder, a future pricing engine, Cart, My Stickr Lab, Control Center, and
   production tooling. Same input → same output, always.

   HARD BOUNDARY: this engine produces NO price. There is no dollar amount, cost,
   or margin here, and `pricing.table` stays null. Sheet pricing is a separate,
   not-yet-approved project.

   Approved production policy:
     - PREFERRED customer sheet TARGET 7.00 in W × 9.00 in H (D-032). This is the
       normal packing surface. The engine packs layouts inside the preferred target
       and trims to the populated block + 0.30 in margin, so finished sheets feel
       intentionally sized instead of always filling the maximum. The preferred
       target is NEVER used merely to increase stickers per sheet.
     - Absolute MAXIMUM customer envelope 8.50 in W × 10.00 in H (D-031). No finished
       sheet may ever exceed this. The engine EXPANDS from the preferred target toward
       the envelope ONLY when a single finished sticker cannot fit the preferred target.
       A sticker that cannot fit even the maximum envelope → unsupported-sheet-product.
     - 0.30 in outer margin; 0.20 in gap (D-030). Official production standards; the
       customer never controls them. Uniform on all four sides / both axes, always.
     - Usable packing area = surface − 2·margin: preferred 6.4 × 8.4, maximum 7.9 × 9.4.
     - 0.0625 in production bleed PER EDGE — a print-time expansion inside the gap; it
       does NOT change the customer's finished size, is NOT added to the pitch, does not
       affect yield, and is production metadata only.
     - 0.30 in trim border (matches the outer margin).
     - Finished sticker side: min 0.50 in, max 10.50 in; sizes snap to 0.25 in.
     - Distribution: fill complete sheets, then ONE remainder sheet trimmed around its
       populated block with the same 0.30 in margin. NO balancing across sheets.
   ============================================================================ */
(function (root) {
  'use strict';

  var PREFERRED_W_IN = 7.0;    // preferred packing target WIDTH  (D-032)
  var PREFERRED_H_IN = 9.0;    // preferred packing target HEIGHT (D-032)
  var ENVELOPE_W_IN  = 8.5;    // absolute maximum finished sheet WIDTH  (D-031)
  var ENVELOPE_H_IN  = 10.0;   // absolute maximum finished sheet HEIGHT (D-031)
  var MARGIN_IN    = 0.30;     // outer margin — official production standard (D-030)
  var GAP_IN       = 0.20;     // gap between stickers — official production standard (D-030)
  /* Customer-UPLOADED sheets only: the smallest gap between two neighbouring final
     production cut paths. Ryan's chosen setting, 8 Oct 2026 (1/8 in). Physical cutting
     at this gap is UNTESTED. Builder layouts, yield and pricing keep GAP_IN. */
  var UPLOAD_GAP_IN = 0.125;
  var TRIM_IN      = 0.30;     // trim border, uniform on every side; matches the outer margin
  /* The FINISHED sheet of a customer-chosen sheet size (D-179). The sheet the customer picks
     is the NOMINAL size: it decides how many stickers fit, with MARGIN_IN and GAP_IN exactly
     as before. What is delivered is trimmed closer: this much from the outermost kiss cut to
     the finished edge, on every side it fits. A side whose stickers fill the nominal sheet
     keeps whatever the nominal sheet leaves (0.30 in for a quarter-inch size). The perimeter
     is a rounded rectangle, cut through on the Graphtec.
     0.20 in since D-198 (Ryan, 22 Oct 2026: about half the old 0.40): the same clearance as
     the gap between stickers. Packing still uses MARGIN_IN, so counts and prices are as
     before; the finished sheet is trimmed closer, and its true size is shown and ordered. */
  var FINISHED_MARGIN_IN = 0.20;
  /* D-199 (Ryan, 22 Oct 2026): a customer-chosen sheet is PACKED with this same 0.20 in edge,
     not MARGIN_IN, so the capacity is what a 0.20 in edge allows (8.5 x 10 -> 8.1 x 9.6 usable).
     With the builder handing in the sticker's REAL kiss-cut size, GAP_IN and this edge are
     distances between real kiss-cut boundaries. MARGIN_IN still serves the legacy path. */
  var SHEET_EDGE_IN = FINISHED_MARGIN_IN;
  var FINISHED_CORNER_IN = 0.15;
  /* A SINGLE STICKER'S BACKING (Ryan, 24 Oct 2026): square or rectangular, with 1/8 in between
     the sticker's outermost edge and the backing cut on every side, so the backing is the
     finished sticker plus 0.25 in each way (3 x 3 in -> 3.25 x 3.25 in). Sharp corners: a
     rounded corner would come closer than 1/8 in to a square sticker's corner. Multi-design
     sheets keep FINISHED_MARGIN_IN and FINISHED_CORNER_IN. */
  var SINGLE_MARGIN_IN = 0.125;
  var SINGLE_CORNER_IN = 0;
  var BLEED_IN     = 0.0625;   // print-time, inside the gap; never added to the pitch
  var MIN_SIDE_IN  = 0.5;
  var MAX_SIDE_IN  = 10.5;
  var SNAP_IN      = 0.25;
  var USABLE_PREF_W_IN = PREFERRED_W_IN - 2 * MARGIN_IN;   // 6.4
  var USABLE_PREF_H_IN = PREFERRED_H_IN - 2 * MARGIN_IN;   // 8.4
  var USABLE_W_IN  = ENVELOPE_W_IN - 2 * MARGIN_IN;        // 7.9 (maximum usable)
  var USABLE_H_IN  = ENVELOPE_H_IN - 2 * MARGIN_IN;        // 9.4 (maximum usable)

  /* ---- Shared sheet-size catalog (D-050) — THE single source of truth for the customer-chosen
     finished sheet size. Desktop AND mobile read this same list; adding/removing a size here
     updates both platforms with no further work. Every entry must fit within the approved
     production envelope (D-031, 8.5" × 10"); 8.5 × 11 stays OUT until separately tested/approved. */
  /* 'single' (D-198, Ryan 22 Oct 2026): ONE kiss-cut sticker on its own closely trimmed
     rectangular backing. It has no fixed size: the backing is the sticker's cut footprint plus
     FINISHED_MARGIN_IN on every side (see singleLayout). The number of sheets is then the number
     of individually backed stickers. It is listed first, on the far left. */
  var SINGLE_SHEET_ID = 'single';
  var SHEET_SIZES = [
    { id: SINGLE_SHEET_ID, widthIn: null, heightIn: null, label: 'Single sticker', single: true },
    { id: '4x6',     widthIn: 4.0, heightIn: 6.0,  label: '4 × 6 in' },
    { id: '8.5x10',  widthIn: 8.5, heightIn: 10.0, label: '8.5 × 10 in' }
  ];
  /* No longer offered (D-198), still READ, so an order or a saved design that names it keeps
     its meaning wherever it is reopened or rebuilt. */
  var RETIRED_SHEET_SIZES = [
    { id: '5.5x8.5', widthIn: 5.5, heightIn: 8.5,  label: '5.5 × 8.5 in', retired: true }
  ];
  var DEFAULT_SHEET_SIZE_ID = '8.5x10';

  /* THE FEWEST SHEETS IN AN ORDER (Ryan, 24 Oct 2026: the "4 x 6 ... 10 minimum" is 10 SHEETS).
     Every sheet order must also reach the pricing engine's 10-sticker minimum (SLPricing QTY_MIN):
     one 4 x 6 sheet of 1.5in stickers holds 6, which priced as "invalid-quantity" and showed no
     price at all. So the floor is the larger of the size's own minimum and the sheets needed for
     10 stickers. A single sticker on backing is counted in stickers (SINGLE_MIN_QTY). Sizes not
     listed keep a minimum of 1 sheet (8.5 x 10 is still Ryan's to set). */
  var MIN_SHEETS_BY_SIZE = { '4x6': 10 };
  var PRICED_MIN_STICKERS = 10;
  function minSheets(id, stickersPerSheet) {
    var s = resolveSheet(id);
    if (s && s.single) return 10;                 // = SINGLE_MIN_QTY, stickers
    var key = s && s.id ? s.id : id;
    var m = MIN_SHEETS_BY_SIZE[key] || 1;
    var per = +stickersPerSheet;
    if (per > 0) m = Math.max(m, Math.ceil(PRICED_MIN_STICKERS / per));
    return m;
  }

  // Resolve a sheet spec (catalog id string, or {widthIn,heightIn} / {w,h}) to a normalized
  // { id, label, widthIn, heightIn }. Returns null for anything unrecognized/invalid. A dimension
  // pair that matches a catalog entry returns that entry (canonical label); otherwise a custom sheet.
  function resolveSheet(spec) {
    if (!spec) return null;
    if (typeof spec === 'string') {
      for (var i = 0; i < SHEET_SIZES.length; i++) if (SHEET_SIZES[i].id === spec) return SHEET_SIZES[i];
      for (var k = 0; k < RETIRED_SHEET_SIZES.length; k++) if (RETIRED_SHEET_SIZES[k].id === spec) return RETIRED_SHEET_SIZES[k];
      return null;
    }
    var w = spec.widthIn != null ? Number(spec.widthIn) : Number(spec.w);
    var h = spec.heightIn != null ? Number(spec.heightIn) : Number(spec.h);
    if (!(w > 0) || !(h > 0)) return null;
    for (var j = 0; j < SHEET_SIZES.length; j++)
      if (SHEET_SIZES[j].widthIn === w && SHEET_SIZES[j].heightIn === h) return SHEET_SIZES[j];
    for (var m = 0; m < RETIRED_SHEET_SIZES.length; m++)
      if (RETIRED_SHEET_SIZES[m].widthIn === w && RETIRED_SHEET_SIZES[m].heightIn === h) return RETIRED_SHEET_SIZES[m];
    return { id: w + 'x' + h, widthIn: round2(w), heightIn: round2(h), label: round2(w) + ' × ' + round2(h) + ' in' };
  }

  function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }
  function snapQuarter(n) { return round2(Math.round(n / SNAP_IN) * SNAP_IN); }

  // Stickers of side `s` that fit along one `usable` axis given gap `g`.
  // N·s + (N−1)·g ≤ usable  ⇒  N ≤ (usable + g)/(s + g). (Approved formula — B4.)
  // Returns 0 when the sticker is wider than the usable axis (s > usable) — i.e. this
  // orientation does not fit that surface at all; the caller treats a 0 as "invalid".
  // The +1e-9 makes an EXACT fit count (e.g. 3×3 + 2·0.20 = 9.4 exactly fits the 9.4
  // usable height): binary floating point renders 9.6/3.2 as 2.9999999996, which would
  // otherwise floor to 2 and silently drop a row. It never over-counts a real non-fit.
  function fitAxis(s, usable, g) { return Math.floor((usable + g) / (s + g) + 1e-9); }

  // Grid for one placement. per = 0 means the sticker does NOT fit this surface in this
  // orientation (so it must not be selected and its trimmed size must not be clamp-faked).
  function grid(w, h, usableW, usableH) {
    var cols = fitAxis(w, usableW, GAP_IN);
    var rows = fitAxis(h, usableH, GAP_IN);
    return { cols: cols, rows: rows, per: (cols > 0 && rows > 0) ? cols * rows : 0 };
  }

  // Trimmed dimensions for a populated block of colsUsed × rowsUsed (B5): block + 2·TRIM,
  // clamped to the sheet SURFACE used (preferred or maximum). For a genuinely-fitting
  // orientation the clamp is a no-op (block ≤ usable ⇒ trimmed ≤ surface).
  function trimmed(colsUsed, rowsUsed, w, h, surfW, surfH) {
    var blockW = colsUsed * w + (colsUsed - 1) * GAP_IN;
    var blockH = rowsUsed * h + (rowsUsed - 1) * GAP_IN;
    return {
      width:  round2(Math.min(surfW, blockW + 2 * TRIM_IN)),
      height: round2(Math.min(surfH, blockH + 2 * TRIM_IN))
    };
  }

  // The layout engine is the single authority on orientation. Evaluate 0° and 90° against
  // the given usable rectangle and choose, in order:
  //   Rule 1/2 — rotate automatically if it increases yield; highest sticker count wins.
  //   Rule 3   — on equal yield, the smaller trimmed sheet (max side, then area).
  //   Rule 4   — still tied, preserve the uploaded artwork orientation.
  // An orientation whose grid.per is 0 does NOT fit this surface and is never chosen.
  // Returns null when NEITHER orientation fits (the sticker is too big for this surface).
  function bestOrientation(w, h, usableW, usableH, surfW, surfH) {
    var g0  = grid(w, h, usableW, usableH);
    var g90 = grid(h, w, usableW, usableH);
    var a = (g0.per  > 0) ? { orientation: 'as-uploaded', w: w, h: h, cols: g0.cols,  rows: g0.rows,  per: g0.per }  : null;
    var b = (g90.per > 0) ? { orientation: 'rotated-90',  w: h, h: w, cols: g90.cols, rows: g90.rows, per: g90.per } : null;
    if (!a && !b) return null;                   // does not fit this surface at all
    if (!a) return b;
    if (!b) return a;
    if (b.per > a.per) return b;                 // Rule 2: higher yield
    if (a.per > b.per) return a;
    var ta = trimmed(a.cols, a.rows, a.w, a.h, surfW, surfH);  // Rule 3: equal yield -> smaller trimmed
    var tb = trimmed(b.cols, b.rows, b.w, b.h, surfW, surfH);
    var maxA = Math.max(ta.width, ta.height), maxB = Math.max(tb.width, tb.height);
    if (maxB < maxA - 1e-9) return b;
    if (maxA < maxB - 1e-9) return a;
    if (tb.width * tb.height < ta.width * ta.height - 1e-9) return b;
    return a;                                    // Rule 4: preserve uploaded orientation
  }

  function fail(status, message, extra) {
    var out = {
      stickersPerSheet: null, fullSheetCount: null, partialSheetStickerCount: null, totalSheetCount: null,
      orientation: null, columns: null, rows: null, trimmedWidth: null, trimmedHeight: null,
      fullTrimmedWidth: null, fullTrimmedHeight: null,
      partialRows: null, partialColumnsUsed: null, partialTrimmedWidth: null, partialTrimmedHeight: null,
      preferredTargetWidth: PREFERRED_W_IN, preferredTargetHeight: PREFERRED_H_IN,
      maximumEnvelopeWidth: ENVELOPE_W_IN, maximumEnvelopeHeight: ENVELOPE_H_IN,
      usedPreferredTarget: null, expandedBeyondPreferred: null,
      status: status, warnings: ((extra && extra.warnings) || []).concat([status]),
      message: message, messaging: null
    };
    if (extra && extra.normalizedWidth != null)  out.normalizedWidth = extra.normalizedWidth;
    if (extra && extra.normalizedHeight != null) out.normalizedHeight = extra.normalizedHeight;
    return out;
  }

  /* ---- chosen-sheet packing (D-050) — the customer explicitly picks the finished SHEET size and
     the NUMBER OF SHEETS. Same orientation/grid/margin math as the production path, packed into the
     chosen surface. The deliverable sheet IS the chosen size (the customer selected it), so there is
     no production trim-down and — because whole sheets are bought — no partial remainder. This engine
     still produces NO price; total stickers = stickersPerSheet × sheets feeds SLPricing.sheet (D-033)
     downstream so pricing stays "by stickers delivered" with one engine. `normW/normH` are the already
     normalized sticker dimensions; `warnings` carries any size-normalization already recorded. */
  /* The finished sheet, and where every sticker sits on it.
     ---------------------------------------------------------------------
     MEASURED FROM THE REAL CUT, NOT THE GRID CELL. The grid packs the size snapped to
     0.25 in; the sticker that is printed and cut is the size the customer ordered. Each
     sticker sits centred in its cell, so the outermost kiss cuts are where the REAL
     stickers end, and the finished margin is taken from there. The same numbers give the
     true gap between neighbouring cuts, which is GAP_IN only when the ordered size is a
     quarter-inch size (it is reported, never adjusted here).

     Coordinates are inches from the finished sheet's top-left corner. `rotated` means
     the sticker is turned 90 degrees on the sheet; widthIn / heightIn are then the
     footprint AS PLACED. */
  function round4(n) { return Math.round((n + Number.EPSILON) * 10000) / 10000; }
  function ceil3(n) { return Math.ceil(n * 1000 - 1e-6) / 1000; }
  function ceil2(n) { return Math.ceil(n * 100 - 1e-6) / 100; }
  function ceil4(n) { return Math.ceil(n * 10000 - 1e-6) / 10000; }
  function finishedSheet(best, actW, actH, SW, SH) {
    var rot = best.orientation === 'rotated-90';
    var realW = rot ? actH : actW, realH = rot ? actW : actH;
    var pitchX = best.w + GAP_IN, pitchY = best.h + GAP_IN;
    var cutW = (best.cols - 1) * pitchX + realW;      // outermost cut to outermost cut
    var cutH = (best.rows - 1) * pitchY + realH;
    /* Rounded UP to the hundredth (D-198), so every side keeps at least FINISHED_MARGIN_IN:
       at 0.20 in, rounding to nearest could have shaved a side to 0.195. */
    /* Exact to 1/10000 in, rounded UP (D-199): a hundredth would have padded an edge by as much
       as 0.005 in. The label still reads to the hundredth. */
    var fw = Math.min(round2(SW), ceil4(cutW + 2 * FINISHED_MARGIN_IN));
    var fh = Math.min(round2(SH), ceil4(cutH + 2 * FINISHED_MARGIN_IN));
    var mx = (fw - cutW) / 2, my = (fh - cutH) / 2;   // centred
    var stickers = [];
    for (var r = 0; r < best.rows; r++) for (var c = 0; c < best.cols; c++)
      stickers.push({ xIn: round4(mx + c * pitchX), yIn: round4(my + r * pitchY) });
    return {
      widthIn: fw, heightIn: fh,
      marginXIn: round4(mx), marginYIn: round4(my),
      targetMarginIn: FINISHED_MARGIN_IN,
      cornerRadiusIn: FINISHED_CORNER_IN,
      cutBlockWidthIn: round4(cutW), cutBlockHeightIn: round4(cutH),
      stickerWidthIn: round4(realW), stickerHeightIn: round4(realH),
      rotated: rot,
      gapXIn: round4(pitchX - realW), gapYIn: round4(pitchY - realH),
      columns: best.cols, rows: best.rows,
      stickers: stickers,
      trimmed: fw < SW - 1e-9 || fh < SH - 1e-9,
      label: fw.toFixed(2) + ' × ' + fh.toFixed(2) + ' in'
    };
  }

  /* SINGLE STICKER (D-198; backing rule 24 Oct 2026): one kiss-cut sticker, upright and centred,
     on a sharp-cornered backing of its cut footprint plus SINGLE_MARGIN_IN (1/8 in) on every
     side (rounded up to the ten-thousandth). The
     backing IS the finished sheet, so nominal and finished agree. Each "sheet" is one sticker:
     totalStickers = sheetQuantity. The backing must fit the production envelope either way up. */
  function singleLayout(sheet, actW, actH, opts, warnings) {
    var fw = ceil4(actW + 2 * SINGLE_MARGIN_IN), fh = ceil4(actH + 2 * SINGLE_MARGIN_IN);
    var fitsEnvelope = (fw <= ENVELOPE_W_IN + 1e-9 && fh <= ENVELOPE_H_IN + 1e-9) ||
                       (fh <= ENVELOPE_W_IN + 1e-9 && fw <= ENVELOPE_H_IN + 1e-9);
    var size = { id: sheet.id, label: sheet.label, widthIn: fw, heightIn: fh, single: true };
    if (!fitsEnvelope) {
      var fu = fail('unsupported-on-sheet', 'this sticker is too large for a single backed sticker',
        { warnings: warnings, normalizedWidth: actW, normalizedHeight: actH });
      fu.sheetSize = size;
      return fu;
    }
    var reqSheets = Number(opts.sheetQuantity);
    var sheetsN = isFinite(reqSheets) ? Math.round(reqSheets) : 1;
    if (isFinite(reqSheets) && sheetsN !== reqSheets) warnings.push('sheet-quantity-normalized');
    if (sheetsN < 1) sheetsN = 1;
    var mx = (fw - actW) / 2, my = (fh - actH) / 2;
    var finished = {
      widthIn: fw, heightIn: fh,
      marginXIn: round4(mx), marginYIn: round4(my),
      targetMarginIn: SINGLE_MARGIN_IN,
      cornerRadiusIn: SINGLE_CORNER_IN,
      cutBlockWidthIn: round4(actW), cutBlockHeightIn: round4(actH),
      stickerWidthIn: round4(actW), stickerHeightIn: round4(actH),
      rotated: false,
      gapXIn: 0, gapYIn: 0,
      columns: 1, rows: 1,
      stickers: [{ xIn: round4(mx), yIn: round4(my) }],
      trimmed: false,
      single: true,
      label: fw.toFixed(2) + ' × ' + fh.toFixed(2) + ' in'
    };
    return {
      stickersPerSheet: 1, fullSheetCount: sheetsN, partialSheetStickerCount: 0, totalSheetCount: sheetsN,
      orientation: 'as-uploaded', columns: 1, rows: 1,
      trimmedWidth: fw, trimmedHeight: fh, fullTrimmedWidth: fw, fullTrimmedHeight: fh,
      status: 'ok', warnings: warnings,
      partialRows: null, partialColumnsUsed: null, partialTrimmedWidth: null, partialTrimmedHeight: null,
      preferredTargetWidth: PREFERRED_W_IN, preferredTargetHeight: PREFERRED_H_IN,
      maximumEnvelopeWidth: ENVELOPE_W_IN, maximumEnvelopeHeight: ENVELOPE_H_IN,
      usedPreferredTarget: false, expandedBeyondPreferred: false,
      normalizedWidth: actW, normalizedHeight: actH,
      sheetMode: 'chosen',
      single: true,
      sheetSize: size,
      sheetQuantity: sheetsN,
      totalStickers: sheetsN,
      finished: finished,
      messaging: {
        count: sheetsN, sheets: sheetsN, perSheet: 1,
        line1: sheetsN + ' single ' + (sheetsN === 1 ? 'sticker' : 'stickers') + ', each on its own backing',
        line2: 'Backing ' + finished.label + ', trimmed 1/8 in from the sticker.'
      }
    };
  }

  function layoutOnSheet(normW, normH, opts, warnings) {
    warnings = warnings ? warnings.slice() : [];
    var sheet = resolveSheet(opts.sheet);
    if (!sheet)
      return fail('invalid-sheet-size', 'unknown sheet size',
        { warnings: warnings, normalizedWidth: normW, normalizedHeight: normH });
    if (sheet.single)
      return singleLayout(sheet,
        isFinite(Number(opts._actualW)) && Number(opts._actualW) > 0 ? Number(opts._actualW) : normW,
        isFinite(Number(opts._actualH)) && Number(opts._actualH) > 0 ? Number(opts._actualH) : normH,
        opts, warnings);

    var SW = sheet.widthIn, SH = sheet.heightIn;
    // Guard: never pack a sheet larger than the approved production envelope (D-031). The catalog is
    // controlled (all entries ≤ 8.5×10) but a custom {w,h} could exceed it — refuse rather than expand.
    if (SW > ENVELOPE_W_IN + 1e-9 || SH > ENVELOPE_H_IN + 1e-9) {
      var fe = fail('unsupported-sheet-product',
        'sheet exceeds the ' + ENVELOPE_W_IN + '" × ' + ENVELOPE_H_IN + '" production envelope',
        { warnings: warnings, normalizedWidth: normW, normalizedHeight: normH });
      fe.sheetSize = { id: sheet.id, label: sheet.label, widthIn: SW, heightIn: SH };
      return fe;
    }

    var usableW = round4(SW - 2 * SHEET_EDGE_IN), usableH = round4(SH - 2 * SHEET_EDGE_IN);   // D-199
    var best = bestOrientation(normW, normH, usableW, usableH, SW, SH);
    if (!best) {
      // The sticker does not fit the chosen sheet in either orientation → guidance, not a crash.
      var fu = fail('unsupported-on-sheet', 'this sticker size does not fit the selected sheet',
        { warnings: warnings, normalizedWidth: normW, normalizedHeight: normH });
      fu.sheetSize = { id: sheet.id, label: sheet.label, widthIn: SW, heightIn: SH };
      return fu;
    }
    if (best.orientation === 'rotated-90') warnings.push('rotated-90');
    var perSheet = best.per;
    if (perSheet === 1) warnings.push('near-full');

    // quantity is expressed as WHOLE sheets (B8-style normalize + warn, never silently change)
    var reqSheets = Number(opts.sheetQuantity);
    var sheetsN = isFinite(reqSheets) ? Math.round(reqSheets) : 1;
    if (isFinite(reqSheets) && sheetsN !== reqSheets) warnings.push('sheet-quantity-normalized');
    if (sheetsN < 1) sheetsN = 1;
    var totalStickers = perSheet * sheetsN;

    // The deliverable sheet IS the chosen size — no trim-down. Dims echoed for preview + cart.
    return {
      // --- approved data contract (same shape as the production path) ---
      stickersPerSheet: perSheet,
      fullSheetCount: sheetsN,
      partialSheetStickerCount: 0,
      totalSheetCount: sheetsN,
      orientation: best.orientation,
      columns: best.cols,
      rows: best.rows,
      trimmedWidth: round2(SW),
      trimmedHeight: round2(SH),
      fullTrimmedWidth: round2(SW),
      fullTrimmedHeight: round2(SH),
      status: 'ok',
      warnings: warnings,
      // --- remainder-sheet production data (none: whole sheets) ---
      partialRows: null, partialColumnsUsed: null, partialTrimmedWidth: null, partialTrimmedHeight: null,
      // --- envelope/target metadata (unchanged constants) ---
      preferredTargetWidth: PREFERRED_W_IN, preferredTargetHeight: PREFERRED_H_IN,
      maximumEnvelopeWidth: ENVELOPE_W_IN, maximumEnvelopeHeight: ENVELOPE_H_IN,
      usedPreferredTarget: false, expandedBeyondPreferred: false,
      // --- normalized finished sticker size echoed (B8) ---
      normalizedWidth: normW, normalizedHeight: normH,
      // --- chosen-sheet additions (D-050) ---
      sheetMode: 'chosen',
      sheetSize: { id: sheet.id, label: sheet.label, widthIn: SW, heightIn: SH },
      sheetQuantity: sheetsN,
      totalStickers: totalStickers,
      // --- the finished, trimmed sheet (D-179). Additive: nothing above changes. ---
      finished: finishedSheet(best,
        isFinite(Number(opts._actualW)) && Number(opts._actualW) > 0 ? Number(opts._actualW) : normW,
        isFinite(Number(opts._actualH)) && Number(opts._actualH) > 0 ? Number(opts._actualH) : normH,
        SW, SH),
      // --- customer-facing informational copy (sheets + per-sheet + total only) ---
      messaging: {
        count: totalStickers,
        sheets: sheetsN,
        perSheet: perSheet,
        line1: sheetsN + ' ' + (sheetsN === 1 ? 'sheet' : 'sheets') + ' · ' + perSheet + ' stickers per sheet',
        line2: 'Approximately ' + totalStickers + ' stickers total.'
      }
      // NOTE: intentionally no price / cost / dollar field — pricing is separate (SLPricing.sheet).
    };
  }

  /* ---- public: layout(widthIn, heightIn, quantity, opts) ----------------------
     Returns the approved data contract (+ partial-sheet production data +
     preferred-target metadata + customer-messaging values). Never returns a price.

     opts.sheet (D-050): a customer-CHOSEN sheet size (catalog id or {widthIn,heightIn}). When
     present, the SAME orientation/grid math packs into that surface and `quantity` is expressed as
     WHOLE sheets via opts.sheetQuantity (see layoutOnSheet). Omit opts → the original production-
     determined behavior (pack 7×9, expand to envelope, trim) is byte-for-byte unchanged. */
  function layout(widthIn, heightIn, quantity, opts) {
    var warnings = [];
    var W = Number(widthIn), H = Number(heightIn), Q = Number(quantity);
    var chosen = !!(opts && opts.sheet);

    if (!isFinite(W) || !isFinite(H) || W <= 0 || H <= 0)
      return fail('invalid-input', 'width and height must be positive numbers');

    // Legacy (production-determined) path buys STICKER quantity → validate it here. Chosen-sheet
    // mode buys whole sheets (opts.sheetQuantity) instead, so the positional quantity is ignored.
    if (!chosen) {
      if (!isFinite(Q)) return fail('invalid-input', 'quantity must be a number');
      // quantity → positive integer (B8: normalize + warn, never silently change)
      var qi = Math.round(Q);
      if (qi !== Q) warnings.push('quantity-normalized');
      if (qi < 1) return fail('invalid-input', 'quantity must be at least 1', { warnings: warnings });
      Q = qi;
    }

    /* A CHOSEN SHEET PACKS THE STICKER AS IT WILL BE CUT (D-179).
       ---------------------------------------------------------------------
       The grid used to be laid out for the size snapped to 0.25 in. A die cut
       ordered at 3 x 1.6 in was packed as 3 x 1.5, so two neighbours' kiss
       cuts sat 0.10 in apart, not 0.20: the gap was a property of the grid
       cell, not of the stickers in it. Here the cell IS the sticker, so
       GAP_IN is the real distance between kiss cuts.

       `cutWidth` / `cutHeight` are the sticker's outline when the caller
       knows it more exactly than the ordered size (a die cut's short side is
       shown to one decimal). Without them the ordered size is the outline.
       Rounded UP to a thousandth, so rounding can only widen a gap. */
    if (chosen) {
      var cw = Number(opts.cutWidth), chh = Number(opts.cutHeight);
      var pw = ceil4((isFinite(cw) && cw > 0) ? cw : W), ph = ceil4((isFinite(chh) && chh > 0) ? chh : H);   // D-199: 1/10000 in
      if (pw < MIN_SIDE_IN || ph < MIN_SIDE_IN)
        return fail('too-small', 'minimum finished sticker dimension is ' + MIN_SIDE_IN + '"',
          { warnings: warnings, normalizedWidth: pw, normalizedHeight: ph });
      if (pw > MAX_SIDE_IN || ph > MAX_SIDE_IN)
        return fail('unsupported-sheet-product', 'maximum finished sticker dimension is ' + MAX_SIDE_IN + '"',
          { warnings: warnings, normalizedWidth: pw, normalizedHeight: ph });
      /* A copy: the caller's opts object is never written to. */
      var o2 = {}; for (var k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) o2[k] = opts[k];
      o2._actualW = pw; o2._actualH = ph;
      return layoutOnSheet(pw, ph, o2, warnings);
    }

    // finished size → 0.25" increments (B8), returning the normalized value + a warning
    var normW = snapQuarter(W), normH = snapQuarter(H);
    if (normW !== round2(W) || normH !== round2(H)) warnings.push('size-normalized');

    // min / max on the finished (normalized) size (B6 / B7)
    if (normW < MIN_SIDE_IN || normH < MIN_SIDE_IN)
      return fail('too-small', 'minimum finished sticker dimension is ' + MIN_SIDE_IN + '"',
        { warnings: warnings, normalizedWidth: normW, normalizedHeight: normH });
    if (normW > MAX_SIDE_IN || normH > MAX_SIDE_IN)
      return fail('unsupported-sheet-product', 'maximum finished sticker dimension is ' + MAX_SIDE_IN + '"',
        { warnings: warnings, normalizedWidth: normW, normalizedHeight: normH });

    // Chosen-sheet mode (D-050): the customer selected the finished sheet size + number of sheets.


    // Pack into the PREFERRED target first (D-032). Only expand toward the maximum envelope
    // when a single sticker cannot fit the preferred target — never merely to raise yield.
    var usedPreferredTarget = true, expandedBeyondPreferred = false;
    var surfW = PREFERRED_W_IN, surfH = PREFERRED_H_IN;
    var best = bestOrientation(normW, normH, USABLE_PREF_W_IN, USABLE_PREF_H_IN, PREFERRED_W_IN, PREFERRED_H_IN);
    if (!best) {
      usedPreferredTarget = false; expandedBeyondPreferred = true;
      surfW = ENVELOPE_W_IN; surfH = ENVELOPE_H_IN;
      warnings.push('expanded-beyond-preferred');
      best = bestOrientation(normW, normH, USABLE_W_IN, USABLE_H_IN, ENVELOPE_W_IN, ENVELOPE_H_IN);
      if (!best)
        return fail('unsupported-sheet-product', 'finished sticker does not fit the ' + ENVELOPE_W_IN + '" × ' + ENVELOPE_H_IN + '" maximum sheet envelope',
          { warnings: warnings, normalizedWidth: normW, normalizedHeight: normH });
    }
    if (best.orientation === 'rotated-90') warnings.push('rotated-90');
    var perSheet = best.per;
    if (perSheet === 1) warnings.push('near-full');

    // sheet counts — fill complete sheets, then one remainder sheet (no balancing, D-031/D-032)
    var fullSheetCount = Math.floor(Q / perSheet);
    var partialSheetStickerCount = Q - fullSheetCount * perSheet;
    var totalSheetCount = Math.ceil(Q / perSheet);

    var full = trimmed(best.cols, best.rows, best.w, best.h, surfW, surfH);

    // remainder sheet: same columns, fewer rows, trimmed shorter around its block (B9)
    var partialRows = null, partialColumnsUsed = null, partialTrimmedWidth = null, partialTrimmedHeight = null;
    if (partialSheetStickerCount > 0) {
      partialRows = Math.ceil(partialSheetStickerCount / best.cols);
      partialColumnsUsed = Math.min(best.cols, partialSheetStickerCount);
      var pt = trimmed(partialColumnsUsed, partialRows, best.w, best.h, surfW, surfH);
      partialTrimmedWidth = pt.width;
      partialTrimmedHeight = pt.height;
    }

    return {
      // --- approved data contract ---
      stickersPerSheet: perSheet,
      fullSheetCount: fullSheetCount,
      partialSheetStickerCount: partialSheetStickerCount,
      totalSheetCount: totalSheetCount,
      orientation: best.orientation,
      columns: best.cols,
      rows: best.rows,
      trimmedWidth: full.width,
      trimmedHeight: full.height,
      fullTrimmedWidth: full.width,
      fullTrimmedHeight: full.height,
      status: 'ok',
      warnings: warnings,
      // --- remainder-sheet production data ---
      partialRows: partialRows,
      partialColumnsUsed: partialColumnsUsed,
      partialTrimmedWidth: partialTrimmedWidth,
      partialTrimmedHeight: partialTrimmedHeight,
      // --- preferred-target metadata (D-032) ---
      preferredTargetWidth: PREFERRED_W_IN,
      preferredTargetHeight: PREFERRED_H_IN,
      maximumEnvelopeWidth: ENVELOPE_W_IN,
      maximumEnvelopeHeight: ENVELOPE_H_IN,
      usedPreferredTarget: usedPreferredTarget,
      expandedBeyondPreferred: expandedBeyondPreferred,
      // --- normalized finished size echoed (B8) ---
      normalizedWidth: normW,
      normalizedHeight: normH,
      // --- customer-facing informational copy (count + sheet total only) ---
      messaging: {
        count: Q,
        sheets: totalSheetCount,
        line1: Q + ' stickers will be professionally arranged across approximately ' + totalSheetCount + ' trimmed sheets.',
        line2: 'Sheets are professionally trimmed to your artwork layout for easier handling and presentation.'
      }
      // NOTE: intentionally no price / cost / dollar field — pricing is separate & unapproved.
    };
  }

  /* ---- pricing boundary: this engine is layout/yield ONLY; no dollars here ----
     Sticker Sheet pricing lives in SLPricing (`SLPricing.sheet` = die-cut × 0.92, D-033),
     derived from the die-cut engine. This layout engine never produces a price. */
  var PRICING = {
    production: false,
    table: null,
    note: 'This engine produces layout/yield only. Sticker Sheet price = die-cut × 0.92 lives in ' +
          'SLPricing.sheet (D-033); do not compute or duplicate sheet prices here.'
  };

  var API = {
    version: 'sheet-layout-v1',
    layout: layout,
    pricing: PRICING,
    // Shared sheet-size catalog (D-050) — THE single source of truth for customer-chosen sheet
    // sizes, read by both desktop and mobile. resolveSheet() maps an id or dims to a catalog entry.
    SHEET_SIZES: SHEET_SIZES,
    RETIRED_SHEET_SIZES: RETIRED_SHEET_SIZES,
    SINGLE_SHEET_ID: SINGLE_SHEET_ID,
    /* The single-sticker mode sells individually backed stickers, priced by SLPricing.sheet on
       the sticker count, whose minimum is 10: these are its sheet-count presets and floor. */
    SINGLE_MIN_QTY: 10,
    SINGLE_QTYS: [10, 25, 50, 100],
    MIN_SHEETS_BY_SIZE: MIN_SHEETS_BY_SIZE,
    minSheets: minSheets,
    isSingle: function (id) { var s = resolveSheet(id); return !!(s && s.single); },
    isOffered: function (id) { for (var i = 0; i < SHEET_SIZES.length; i++) if (SHEET_SIZES[i].id === id) return true; return false; },
    DEFAULT_SHEET_SIZE_ID: DEFAULT_SHEET_SIZE_ID,
    resolveSheet: resolveSheet,
    constants: {
      PREFERRED_W_IN: PREFERRED_W_IN, PREFERRED_H_IN: PREFERRED_H_IN,
      ENVELOPE_W_IN: ENVELOPE_W_IN, ENVELOPE_H_IN: ENVELOPE_H_IN,
      MARGIN_IN: MARGIN_IN, GAP_IN: GAP_IN, UPLOAD_GAP_IN: UPLOAD_GAP_IN, TRIM_IN: TRIM_IN,
      FINISHED_MARGIN_IN: FINISHED_MARGIN_IN, FINISHED_CORNER_IN: FINISHED_CORNER_IN, SHEET_EDGE_IN: SHEET_EDGE_IN,
      SINGLE_MARGIN_IN: SINGLE_MARGIN_IN, SINGLE_CORNER_IN: SINGLE_CORNER_IN,
      BLEED_IN: BLEED_IN, MIN_SIDE_IN: MIN_SIDE_IN, MAX_SIDE_IN: MAX_SIDE_IN, SNAP_IN: SNAP_IN,
      USABLE_PREF_W_IN: USABLE_PREF_W_IN, USABLE_PREF_H_IN: USABLE_PREF_H_IN,
      USABLE_W_IN: USABLE_W_IN, USABLE_H_IN: USABLE_H_IN
    },
    // exposed for tests/tooling — not for customer display. Evaluates a placement against
    // an arbitrary usable rectangle (surface defaults to usable + 2·margin, so no clamping).
    _fitOrientations: function (w, h, usableW, usableH) {
      usableW = usableW || USABLE_PREF_W_IN; usableH = usableH || USABLE_PREF_H_IN;
      var sW = usableW + 2 * MARGIN_IN, sH = usableH + 2 * MARGIN_IN;
      return { as0: grid(w, h, usableW, usableH), as90: grid(h, w, usableW, usableH), best: bestOrientation(w, h, usableW, usableH, sW, sH) };
    }
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (root) root.SLSheets = API;
})(typeof window !== 'undefined' ? window : null);
